import React, { useState } from "react";
import { Drawer, List, Button, Input, Popconfirm, Empty, Typography } from "antd";
import {
    SearchOutlined,
    DeleteOutlined,
    PlusOutlined,
    CloseOutlined,
    HistoryOutlined,
    FileSearchOutlined,
} from "@ant-design/icons";
import moment from "moment";
import "./ChatHistorySidebar.css"; // Reuse the same styles as chat history
import lang from "../helper/langHelper";

const { Text } = Typography;

const ResearchHistorySidebar = ({
    open,
    onClose,
    researchHistories,
    onSelectResearch,
    onDeleteResearch,
    onClearAll,
    onCreateNew,
    currentResearchId,
}) => {
    const [searchText, setSearchText] = useState("");

    const filteredHistories = researchHistories.filter((history) =>
        history.title.toLowerCase().includes(searchText.toLowerCase())
    );

    const formatDate = (dateString) => {
        const date = moment(dateString);
        const now = moment();

        if (date.isSame(now, "day")) {
            return date.format("h:mm A");
        } else if (date.isSame(now, "week")) {
            return date.format("ddd h:mm A");
        } else {
            return date.format("MMM D, YYYY");
        }
    };

    return (
        <Drawer
            title={
                <div className="chat-history-header">
                    <div className="chat-history-header-top">
                        <HistoryOutlined style={{ fontSize: 18, marginRight: 8 }} />
                        <span>{lang("Prompt History")}</span>
                    </div>
                    <Button
                        type="text"
                        icon={<CloseOutlined />}
                        onClick={onClose}
                        className="close-drawer-btn"
                    />
                </div>
            }
            placement="left"
            closable={false}
            onClose={onClose}
            open={open}
            width={280}
            className="chat-history-drawer"
            bodyStyle={{ padding: 0 }}
        >
            <div className="chat-history-sidebar">
                {/* New Research Button */}
                <div className="chat-history-actions">
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        block
                        onClick={onCreateNew}
                        className="new-chat-btn"
                    >
                        {lang("New Research")}
                    </Button>
                </div>

                {/* Search Input */}
                <div className="chat-history-search">
                    <Input
                        placeholder={lang("Search prompts...")}
                        prefix={<SearchOutlined />}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        allowClear
                    />
                </div>

                {/* Research List */}
                <div className="chat-history-list-container">
                    {filteredHistories.length === 0 ? (
                        <div className="chat-history-empty">
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    searchText ? lang("No prompts found") : lang("No prompt history yet")
                                }
                            />
                        </div>
                    ) : (
                        <List
                            dataSource={filteredHistories}
                            renderItem={(history) => (
                                <List.Item
                                    className={`chat-history-item ${currentResearchId === history.id ? "active" : ""
                                        }`}
                                    onClick={() => onSelectResearch(history)}
                                >
                                    <div className="chat-history-item-content">
                                        <div className="chat-history-item-header">
                                            <FileSearchOutlined className="chat-icon" />
                                            <div className="chat-history-item-title">
                                                <Text ellipsis={{ tooltip: history.title }}>
                                                    {history.title}
                                                </Text>
                                            </div>
                                        </div>
                                        <div className="chat-history-item-footer">
                                            <span className="chat-history-date">
                                                {formatDate(history.updatedAt)}
                                            </span>
                                            <Popconfirm
                                                title={lang("Delete this prompt?")}
                                                description={lang("This action cannot be undone.")}
                                                onConfirm={(e) => {
                                                    e.stopPropagation();
                                                    onDeleteResearch(history.id);
                                                }}
                                                onCancel={(e) => e.stopPropagation()}
                                                okText={lang("Delete")}
                                                cancelText={lang("Cancel")}
                                                okButtonProps={{ danger: true }}
                                            >
                                                <Button
                                                    type="text"
                                                    icon={<DeleteOutlined />}
                                                    size="small"
                                                    className="delete-chat-btn"
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </Popconfirm>
                                        </div>
                                    </div>
                                </List.Item>
                            )}
                        />
                    )}
                </div>

                {/* Clear All Button */}
                {researchHistories.length > 0 && (
                    <div className="chat-history-footer">
                        <Popconfirm
                            title={lang("Clear all prompt history?")}
                            description={lang(
                                "This will delete all your saved prompts. This action cannot be undone."
                            )}
                            onConfirm={onClearAll}
                            okText={lang("Clear All")}
                            cancelText={lang("Cancel")}
                            okButtonProps={{ danger: true }}
                        >
                            <Button
                                type="text"
                                danger
                                block
                                className="clear-all-btn"
                            >
                                {lang("Clear All History")}
                            </Button>
                        </Popconfirm>
                    </div>
                )}
            </div>
        </Drawer>
    );
};

export default ResearchHistorySidebar;
